import type { EmojiItem } from "@tiptap/extension-emoji"

const MAX_SUGGESTIONS = 100
const SORT_EMOJIS = <T extends EmojiItem>(a: T, b: T) =>
  a.name.localeCompare(b.name)

const parseEmojiFromFallbackImage = (fallbackImage?: string): string | null => {
  if (!fallbackImage) return null

  try {
    const filename = fallbackImage.split("/").pop()?.replace(".png", "")
    if (!filename) return null

    const codepoints = filename
      .split("-")
      .map((part) => Number.parseInt(part, 16))
      .filter((value) => Number.isFinite(value))

    if (codepoints.length === 0) return null

    return String.fromCodePoint(...codepoints)
  } catch {
    return null
  }
}

export const getDisplayEmoji = <T extends EmojiItem>(emoji: T): string => {
  return parseEmojiFromFallbackImage(emoji.fallbackImage) || emoji.emoji
}


export const searchEmojiData = <T extends EmojiItem>(
  query: string,
  emojiData: T
): boolean => {
  const lowercaseQuery = query.toLowerCase().trim()

  return (
    emojiData.name.toLowerCase().includes(lowercaseQuery) ||
    emojiData.shortcodes.some((shortName) =>
      shortName.toLowerCase().includes(lowercaseQuery)
    ) ||
    emojiData.tags.some((tag) => tag.toLowerCase().includes(lowercaseQuery))
  )
}

export const getFilteredEmojis = <T extends EmojiItem>(props: {
  query: string
  emojis: T[]
}) => {
  const { query, emojis } = props
  const trimmedQuery = query.trim()

  const filteredEmojis = !trimmedQuery
    ? emojis.slice(0, MAX_SUGGESTIONS)
    : emojis
        .filter((emoji) => searchEmojiData(trimmedQuery, emoji))
        .slice(0, MAX_SUGGESTIONS)

  return filteredEmojis.sort(SORT_EMOJIS)
}
